import React from 'react';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { getEst3CostProvenance } from './est3CostProvenance';
import {
  EST3_EQUIPMENT_COMPONENT_KINDS,
  EST3_EQUIPMENT_DURATION_UOMS,
  summarizeEquipmentComponents,
} from './est3EquipmentKinds';

const { Text, Title } = Typography;

export interface EpcEstimateEquipmentBuildUpPanelProps {
  estimateId: string;
  revisionId: string;
  costLineId: string | null;
  costLine?: Record<string, unknown> | null;
  readOnly?: boolean;
  onChanged?: () => void;
  compact?: boolean;
}

/**
 * EST3 UX2 Wave 5 — Equipment / plant component build-up.
 */
export const EpcEstimateEquipmentBuildUpPanel: React.FC<EpcEstimateEquipmentBuildUpPanelProps> = ({
  estimateId,
  revisionId,
  costLineId,
  costLine,
  readOnly,
  onChanged,
  compact,
}) => {
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const compQ = useQuery({
    queryKey: ['epc-est-eq-comp', estimateId, costLineId],
    queryFn: () => epcEstimationService.listEquipmentComponents(estimateId, costLineId!),
    enabled: !!estimateId && !!costLineId,
  });

  const classesQ = useQuery({
    queryKey: ['epc-est-eq-classes', estimateId],
    queryFn: () => epcEstimationService.listEquipmentClasses(estimateId),
    enabled: !!estimateId,
  });

  const createM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createEquipmentComponent(estimateId, costLineId!, { revisionId, ...v }),
    onSuccess: () => {
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-eq-comp', estimateId, costLineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  const deleteM = useMutation({
    mutationFn: (componentId: string) =>
      epcEstimationService.deleteEquipmentComponent(estimateId, costLineId!, componentId, revisionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['epc-est-eq-comp', estimateId, costLineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  if (!costLineId) {
    return <Empty description="Select an equipment cost line to view plant build-up." />;
  }

  const components = compQ.data || [];
  const { byKind, plantTotal } = summarizeEquipmentComponents(components);
  const lineProv = costLine ? getEst3CostProvenance(costLine) : null;
  const classOptions = (classesQ.data || []).map((c: any) => ({
    value: c.id,
    label: `${c.code} — ${c.name}`,
  }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {costLine ? (
        <Descriptions size="small" column={compact ? 1 : 2} bordered>
          <Descriptions.Item label="Cost line">{String(costLine.description || '—')}</Descriptions.Item>
          <Descriptions.Item label="Line total">
            {formatMoney(costLine.total_amount, { display: 'plain', maximumFractionDigits: 2 })}
          </Descriptions.Item>
          {lineProv && lineProv.summary !== '—' ? (
            <Descriptions.Item label="Line provenance" span={2}>
              {lineProv.summary}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      ) : null}

      <Card size="small" title="Plant cost breakdown">
        {byKind.length === 0 ? (
          <Text type="secondary">No components — add equipment classes to build up plant cost.</Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {byKind.map((row) => (
              <div
                key={row.kind}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}
              >
                <Tag>{row.kind}</Tag>
                <Text>{formatMoney(row.amount, { display: 'plain', maximumFractionDigits: 2 })}</Text>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 600,
                borderTop: '1px solid #f0f0f0',
                paddingTop: 8,
              }}
            >
              <Text>Plant total</Text>
              <Text>{formatMoney(plantTotal, { display: 'plain', maximumFractionDigits: 2 })}</Text>
            </div>
          </Space>
        )}
      </Card>

      {!readOnly ? (
        <Card size="small" title="Add equipment component">
          <Form
            form={form}
            layout="vertical"
            onFinish={(v) => createM.mutate(v)}
            initialValues={{ componentKind: 'BASE', quantity: 1, duration: 1, durationUom: 'DAY' }}
          >
            <Form.Item name="equipmentClassId" label="Equipment class" rules={[{ required: true }]}>
              <Select options={classOptions} showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="componentKind" label="Kind">
              <Select options={EST3_EQUIPMENT_COMPONENT_KINDS.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
            <Space wrap style={{ width: '100%' }}>
              <Form.Item name="quantity" label="Qty">
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item name="duration" label="Duration">
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item name="durationUom" label="Duration UoM">
                <Select
                  style={{ width: 110 }}
                  options={EST3_EQUIPMENT_DURATION_UOMS.map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
              <Form.Item name="rate" label="Rate">
                <InputNumber min={0} placeholder="Blank = library" />
              </Form.Item>
            </Space>
            <Form.Item name="sourceRefLabel" label="Provenance label">
              <Input placeholder="e.g. Hire quote EQ-2201" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={createM.isPending} block>
              Add equipment component
            </Button>
          </Form>
        </Card>
      ) : null}

      <Card
        size="small"
        title={
          <Space>
            <Title level={5} style={{ margin: 0 }}>
              Components
            </Title>
            <Tag>{components.length}</Tag>
          </Space>
        }
      >
        <Table
          size="small"
          rowKey="id"
          loading={compQ.isLoading}
          dataSource={components}
          pagination={components.length > 20 ? { pageSize: 20 } : false}
          scroll={{ x: true }}
          columns={[
            { title: 'Class', dataIndex: 'equipment_class_code', width: 90 },
            { title: 'Kind', dataIndex: 'component_kind', width: 80 },
            { title: 'Qty', dataIndex: 'quantity', width: 50 },
            { title: 'Dur', dataIndex: 'duration', width: 60 },
            { title: 'UoM', dataIndex: 'duration_uom', width: 70 },
            {
              title: 'Amount',
              dataIndex: 'amount',
              width: 90,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Prov.',
              width: 110,
              ellipsis: true,
              render: (_: unknown, row: any) => {
                const p = getEst3CostProvenance(row);
                return p.summary !== '—' ? (
                  <Tag color="blue">{p.summary}</Tag>
                ) : (
                  <Text type="secondary">{row.rate_source || '—'}</Text>
                );
              },
            },
            ...(!readOnly
              ? [
                  {
                    title: '',
                    width: 70,
                    render: (_: unknown, row: any) => (
                      <Popconfirm title="Remove component?" onConfirm={() => deleteM.mutate(row.id)}>
                        <Button size="small" danger loading={deleteM.isPending}>
                          Del
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </Space>
  );
};
